import type { ListBuilderList } from '../../../types';
import {
	accountColumn,
	boardActionColumn,
	licenseColumn,
	reviewedActionColumn,
	reviewNoteColumn,
	reviewOutcomeColumn,
	salesColumn,
} from '../columns';
import { hideOtherCardFilters } from '../filter-rules';
import { COMPLIANCE_ATTENTION_LIST_IDS } from '../list-ids';

/**
 * Adverse Action — REVIEWED (PD-16615). The items an officer's decision is
 * hiding from the Adverse and Flag Only lists, in either group: the decision,
 * its note, who recorded it and when, and — first — the follow-ups coming
 * due. Before this list a marked-off item simply vanished; the only way back
 * to it was to reopen its modal from a list it was no longer on.
 *
 * An item leaves this list the moment its decision stops hiding it: the board
 * changes its record, the follow-up date arrives, or the officer re-opens it
 * (the modal's Re-open, the undo). It is then back on its own list.
 *
 * COUNT PARITY. The locked `licenseAdverseInForce: reviewed` is the same
 * in-force fragment narrowed to the reviewed set the other two lists and the
 * dashboard card net out, so the three lists partition one population.
 */
export const adverseReviewedList: ListBuilderList = {
	id: COMPLIANCE_ATTENTION_LIST_IDS.adverseReviewed,
	cacheTTL: 300000,
	name: 'Reviewed',
	type: 'default',
	active: true,
	sortOrder: 4,
	filterRules: {
		...hideOtherCardFilters(['licenseAdverseInForce']),
		licenseActiveBuyer: {
			defaultValue: [{ id: 'yes', label: 'Buying (sale in 90 days)' }],
			locked: true,
		},
		licenseAdverseInForce: {
			defaultValue: [
				{ id: 'reviewed', label: 'Reviewed, hidden until the board changes' },
			],
			locked: true,
		},
	},
	columns: [
		accountColumn,
		salesColumn,
		licenseColumn,
		boardActionColumn,
		reviewOutcomeColumn,
		reviewNoteColumn,
		reviewedActionColumn,
	],
	// Follow-ups coming due first, then the largest accounts. Curated here
	// rather than added to the shared sorts: a follow-up date means nothing on
	// any other list.
	sortOptions: [
		{
			id: 'follow-up-soonest',
			name: 'Follow-Up Date (Soonest First)',
			description: 'Follow-ups coming due first, then the other decisions',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'adverseFollowUpDate',
					objectTable: 'client_license',
				},
				{
					direction: 'DESC',
					fieldName: 'clientRevenue90',
					objectTable: 'client_license',
				},
			],
		},
		{
			id: 'sales-90-desc',
			name: 'Sales 90 Days (High to Low)',
			description: 'Largest accounts first',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'clientRevenue90',
					objectTable: 'client_license',
				},
				{
					direction: 'ASC',
					fieldName: 'clientName',
					objectTable: 'client_license',
				},
			],
		},
	],
	defaultSortId: 'follow-up-soonest',
};
